import React from "react";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useTheme,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import {
  formatCurrency,
  formatNumber,
  formatPercentage,
  formatPercentageNoMultiply,
} from "../../utils/formatUtils";
import { components } from "../../types/api";
import OrbLoader from "../common/OrbLoader";
import { useSelector } from "react-redux";
import { selectUserDb } from "../../store/slices/session/sessionSelector";

interface LineItem {
  id?: string;
  name?: string;
  dailyData: components["schemas"]["DailyLineItemMetrics"][];
  summary: components["schemas"]["MetricsLineItemSummary"];
}

interface LineItemsTableProps {
  lineItems: LineItem[] | undefined;
  isLoading: boolean;
  maxHeight?: string | number;
  stickyHeader?: boolean;
  selectedLineItemId: string | undefined;
  onLineItemClick: (lineItem: LineItem) => void;
  currency?: components["schemas"]["currency"];
  format?: "DISPLAY" | "NATIVE" | "VIDEO" | "ALL" | "AUDIO";
  ctrIsPreScaled?: boolean;
  useTotalsForCtr?: boolean;
}

const LineItemsTable: React.FC<LineItemsTableProps> = ({
  lineItems,
  isLoading,
  maxHeight = 400,
  stickyHeader = true,
  selectedLineItemId,
  onLineItemClick,
  currency = "EUR",
  format = "ALL",
  ctrIsPreScaled = false,
  useTotalsForCtr = false,
}) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const userDb = useSelector(selectUserDb);
  const isAdmin = userDb?.role === "ADMIN";
  const showVideoMetrics = format === "VIDEO";
  const showAudioMetrics = format === "AUDIO";

  const calculateCtr = (
    summary: components["schemas"]["MetricsLineItemSummary"]
  ) => {
    if (!useTotalsForCtr) {
      const value = summary.avgCTR ?? 0;
      return ctrIsPreScaled
        ? formatPercentageNoMultiply(value)
        : formatPercentage(value);
    }

    const totalImpressions = summary.totalImpressions ?? 0;
    const totalClicks = summary.totalClicks ?? 0;
    const ctrValue = totalImpressions > 0 ? totalClicks / totalImpressions : 0;

    return formatPercentage(ctrValue);
  };

  const getAvgCPV = (
    summary: components["schemas"]["MetricsLineItemSummary"]
  ) => {
    const value = (summary as Record<string, unknown>).avgCPV;
    return typeof value === "number" ? value : 0;
  };

  const getIndexListenComplete = (
    summary: components["schemas"]["MetricsLineItemSummary"]
  ) => {
    const totalCompleteViews = summary.totalCompleteViews ?? 0;
    const totalImpressions = summary.totalImpressions ?? 0;
    const ratio =
      totalImpressions > 0 ? totalCompleteViews / totalImpressions : 0;

    return formatPercentage(ratio);
  };

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
        <OrbLoader
          size="large"
          label={t("common.loading", { defaultValue: "Cargando…" })}
        />
      </Box>
    );
  }

  if (!lineItems || lineItems.length === 0) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="body1" color="text.secondary">
          {t("campaigns.noLineItems", {
            defaultValue: "No line items available for this campaign",
          })}
        </Typography>
      </Box>
    );
  }

  return (
    <TableContainer
      component={Paper}
      sx={{
        boxShadow: "none",
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 2,
        maxHeight: maxHeight,
        overflow: "auto",
        mt: 3,
      }}
    >
      <Table stickyHeader={stickyHeader} size="small">
        <TableHead>
          <TableRow>
            <TableCell
              sx={{
                fontWeight: 600,
                backgroundColor: theme.palette.background.default,
                borderBottom: `2px solid ${theme.palette.divider}`,
                color: theme.palette.text.primary,
                fontSize: "0.875rem",
                py: 1.5,
              }}
            >
              {t("campaigns.name", { defaultValue: "Name" })}
            </TableCell>
            <TableCell
              sx={{
                fontWeight: 600,
                backgroundColor: theme.palette.background.default,
                borderBottom: `2px solid ${theme.palette.divider}`,
                color: theme.palette.text.primary,
                fontSize: "0.875rem",
                py: 1.5,
              }}
            >
              {t("campaigns.impressions", { defaultValue: "Impressions" })}
            </TableCell>
            <TableCell
              sx={{
                fontWeight: 600,
                backgroundColor: theme.palette.background.default,
                borderBottom: `2px solid ${theme.palette.divider}`,
                color: theme.palette.text.primary,
                fontSize: "0.875rem",
                py: 1.5,
              }}
            >
              {t("campaigns.clicks", { defaultValue: "Clicks" })}
            </TableCell>
            <TableCell
              sx={{
                fontWeight: 600,
                backgroundColor: theme.palette.background.default,
                borderBottom: `2px solid ${theme.palette.divider}`,
                color: theme.palette.text.primary,
                fontSize: "0.875rem",
                py: 1.5,
              }}
            >
              {t("campaigns.ctr", { defaultValue: "CTR" })}
            </TableCell>
            <TableCell
              sx={{
                fontWeight: 600,
                backgroundColor: theme.palette.background.default,
                borderBottom: `2px solid ${theme.palette.divider}`,
                color: theme.palette.text.primary,
                fontSize: "0.875rem",
                py: 1.5,
              }}
            >
              {t("campaigns.cpm", { defaultValue: "CPM" })}
            </TableCell>
            <TableCell
              sx={{
                fontWeight: 600,
                backgroundColor: theme.palette.background.default,
                borderBottom: `2px solid ${theme.palette.divider}`,
                color: theme.palette.text.primary,
                fontSize: "0.875rem",
                py: 1.5,
              }}
            >
              {t("campaigns.cpc", { defaultValue: "CPC" })}
            </TableCell>
            <TableCell
              sx={{
                fontWeight: 600,
                backgroundColor: theme.palette.background.default,
                borderBottom: `2px solid ${theme.palette.divider}`,
                color: theme.palette.text.primary,
                fontSize: "0.875rem",
                py: 1.5,
              }}
            >
              {t("campaigns.cost", { defaultValue: "Cost" })}
            </TableCell>
            {/* Add video-specific columns if format is VIDEO */}
            {showVideoMetrics && (
              <>
                <TableCell
                  sx={{
                    fontWeight: 600,
                    backgroundColor: theme.palette.background.default,
                    borderBottom: `2px solid ${theme.palette.divider}`,
                    color: theme.palette.text.primary,
                    fontSize: "0.875rem",
                    py: 1.5,
                  }}
                >
                  {t("campaigns.vtr", { defaultValue: "VTR" })}
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: 600,
                    backgroundColor: theme.palette.background.default,
                    borderBottom: `2px solid ${theme.palette.divider}`,
                    color: theme.palette.text.primary,
                    fontSize: "0.875rem",
                    py: 1.5,
                  }}
                >
                  {t("campaigns.firstQuartile", {
                    defaultValue: "First Quartile",
                  })}
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: 600,
                    backgroundColor: theme.palette.background.default,
                    borderBottom: `2px solid ${theme.palette.divider}`,
                    color: theme.palette.text.primary,
                    fontSize: "0.875rem",
                    py: 1.5,
                  }}
                >
                  {t("campaigns.secondQuartile", {
                    defaultValue: "Second Quartile",
                  })}
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: 600,
                    backgroundColor: theme.palette.background.default,
                    borderBottom: `2px solid ${theme.palette.divider}`,
                    color: theme.palette.text.primary,
                    fontSize: "0.875rem",
                    py: 1.5,
                  }}
                >
                  {t("campaigns.thirdQuartile", {
                    defaultValue: "Third Quartile",
                  })}
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: 600,
                    backgroundColor: theme.palette.background.default,
                    borderBottom: `2px solid ${theme.palette.divider}`,
                    color: theme.palette.text.primary,
                    fontSize: "0.875rem",
                    py: 1.5,
                  }}
                >
                  {t("campaigns.totalConversions", {
                    defaultValue: "Conversions",
                  })}
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: 600,
                    backgroundColor: theme.palette.background.default,
                    borderBottom: `2px solid ${theme.palette.divider}`,
                    color: theme.palette.text.primary,
                    fontSize: "0.875rem",
                    py: 1.5,
                  }}
                >
                  {t("campaigns.uniqueUsers", { defaultValue: "Unique Users" })}
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: 600,
                    backgroundColor: theme.palette.background.default,
                    borderBottom: `2px solid ${theme.palette.divider}`,
                    color: theme.palette.text.primary,
                    fontSize: "0.875rem",
                    py: 1.5,
                  }}
                >
                  {t("campaigns.completeViews", {
                    defaultValue: "Complete Views",
                  })}
                </TableCell>
              </>
            )}
            {showAudioMetrics && (
              <>
                <TableCell
                  sx={{
                    fontWeight: 600,
                    backgroundColor: theme.palette.background.default,
                    borderBottom: `2px solid ${theme.palette.divider}`,
                    color: theme.palette.text.primary,
                    fontSize: "0.875rem",
                    py: 1.5,
                  }}
                >
                  {t("campaigns.cpv", { defaultValue: "CPV" })}
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: 600,
                    backgroundColor: theme.palette.background.default,
                    borderBottom: `2px solid ${theme.palette.divider}`,
                    color: theme.palette.text.primary,
                    fontSize: "0.875rem",
                    py: 1.5,
                  }}
                >
                  {t("campaigns.listenComplete", {
                    defaultValue: "Listen Complete",
                  })}
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: 600,
                    backgroundColor: theme.palette.background.default,
                    borderBottom: `2px solid ${theme.palette.divider}`,
                    color: theme.palette.text.primary,
                    fontSize: "0.875rem",
                    py: 1.5,
                  }}
                >
                  {t("campaigns.indexListenComplete", {
                    defaultValue: "Index Listen Complete",
                  })}
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: 600,
                    backgroundColor: theme.palette.background.default,
                    borderBottom: `2px solid ${theme.palette.divider}`,
                    color: theme.palette.text.primary,
                    fontSize: "0.875rem",
                    py: 1.5,
                  }}
                >
                  {t("campaigns.totalReach", { defaultValue: "Reach" })}
                </TableCell>
              </>
            )}
            {/* Add additional metrics for all formats */}
            <TableCell
              sx={{
                fontWeight: 600,
                backgroundColor: theme.palette.background.default,
                borderBottom: `2px solid ${theme.palette.divider}`,
                color: theme.palette.text.primary,
                fontSize: "0.875rem",
                py: 1.5,
              }}
            >
              {t("campaigns.measurableImpressions", {
                defaultValue: "Measurable Impressions",
              })}
            </TableCell>
            <TableCell
              sx={{
                fontWeight: 600,
                backgroundColor: theme.palette.background.default,
                borderBottom: `2px solid ${theme.palette.divider}`,
                color: theme.palette.text.primary,
                fontSize: "0.875rem",
                py: 1.5,
              }}
            >
              {t("campaigns.engagementRate", {
                defaultValue: "Engagement Rate",
              })}
            </TableCell>
            <TableCell
              sx={{
                fontWeight: 600,
                backgroundColor: theme.palette.background.default,
                borderBottom: `2px solid ${theme.palette.divider}`,
                color: theme.palette.text.primary,
                fontSize: "0.875rem",
                py: 1.5,
              }}
            >
              {t("campaigns.interactionTime", {
                defaultValue: "Interaction Time",
              })}
            </TableCell>
            <TableCell
              sx={{
                fontWeight: 600,
                backgroundColor: theme.palette.background.default,
                borderBottom: `2px solid ${theme.palette.divider}`,
                color: theme.palette.text.primary,
                fontSize: "0.875rem",
                py: 1.5,
              }}
            >
              {t("campaigns.totalReach", { defaultValue: "Total Reach" })}
            </TableCell>
            {isAdmin && (
              <TableCell
                sx={{
                  fontWeight: 600,
                  backgroundColor: theme.palette.background.default,
                  borderBottom: `2px solid ${theme.palette.divider}`,
                  color: theme.palette.text.primary,
                  fontSize: "0.875rem",
                  py: 1.5,
                }}
              >
                {t("campaigns.realCost", { defaultValue: "Real Cost" })}
              </TableCell>
            )}
          </TableRow>
        </TableHead>
        <TableBody>
          {lineItems.map((lineItem) => (
            <TableRow
              key={lineItem.id || lineItem.name}
              onClick={() => onLineItemClick(lineItem)}
              hover
              selected={selectedLineItemId === lineItem.id}
              sx={{
                cursor: "pointer",
                borderBottom: `1px solid ${theme.palette.divider}`,
                "&:last-child td, &:last-child th": { border: 0 },
                backgroundColor:
                  selectedLineItemId === lineItem.id
                    ? theme.palette.neutral["200"]
                    : "inherit",
                transition: "background-color 0.3s ease",
                "&:hover": {
                  backgroundColor: theme.palette.neutral["100"],
                },
                height: "52px",
              }}
            >
              <TableCell sx={{ fontSize: "0.875rem", py: 1.5 }}>
                <Typography
                  variant="body2"
                  fontWeight={500}
                  color="text.primary"
                >
                  {lineItem.name ||
                    t("campaigns.unnamed", {
                      defaultValue: "Unnamed Line Item",
                    })}
                </Typography>
              </TableCell>
              <TableCell sx={{ fontSize: "0.875rem", py: 1.5 }}>
                {formatNumber(lineItem.summary.totalImpressions)}
              </TableCell>
              <TableCell sx={{ fontSize: "0.875rem", py: 1.5 }}>
                {formatNumber(lineItem.summary.totalClicks)}
              </TableCell>
              <TableCell sx={{ fontSize: "0.875rem", py: 1.5 }}>
                {calculateCtr(lineItem.summary)}
              </TableCell>
              <TableCell sx={{ fontSize: "0.875rem", py: 1.5 }}>
                {formatCurrency(lineItem.summary.avgCPM, currency)}
              </TableCell>
              <TableCell sx={{ fontSize: "0.875rem", py: 1.5 }}>
                {formatCurrency(lineItem.summary.avgCPC, currency)}
              </TableCell>
              <TableCell sx={{ fontSize: "0.875rem", py: 1.5 }}>
                {formatCurrency(lineItem.summary.totalCost, currency)}
              </TableCell>
              {/* Add video-specific data if format is VIDEO */}
              {showVideoMetrics && (
                <>
                  <TableCell sx={{ fontSize: "0.875rem", py: 1.5 }}>
                    {formatPercentage(lineItem.summary.avgVTR || 0)}
                  </TableCell>
                  <TableCell sx={{ fontSize: "0.875rem", py: 1.5 }}>
                    {formatNumber(
                      lineItem.summary.totalFirstQuartileViews || 0
                    )}
                  </TableCell>
                  <TableCell sx={{ fontSize: "0.875rem", py: 1.5 }}>
                    {formatNumber(lineItem.summary.totalMidpointViews || 0)}
                  </TableCell>
                  <TableCell sx={{ fontSize: "0.875rem", py: 1.5 }}>
                    {formatNumber(
                      lineItem.summary.totalThirdQuartileViews || 0
                    )}
                  </TableCell>
                  <TableCell sx={{ fontSize: "0.875rem", py: 1.5 }}>
                    {formatNumber(lineItem.summary.totalConversions || 0)}
                  </TableCell>
                  <TableCell sx={{ fontSize: "0.875rem", py: 1.5 }}>
                    {formatNumber(lineItem.summary.totalUniqueUsers || 0)}
                  </TableCell>
                  <TableCell sx={{ fontSize: "0.875rem", py: 1.5 }}>
                    {formatNumber(lineItem.summary.totalCompleteViews || 0)}
                  </TableCell>
                </>
              )}
              {showAudioMetrics && (
                <>
                  <TableCell sx={{ fontSize: "0.875rem", py: 1.5 }}>
                    {formatCurrency(getAvgCPV(lineItem.summary), currency)}
                  </TableCell>
                  <TableCell sx={{ fontSize: "0.875rem", py: 1.5 }}>
                    {formatNumber(lineItem.summary.totalCompleteViews || 0)}
                  </TableCell>
                  <TableCell sx={{ fontSize: "0.875rem", py: 1.5 }}>
                    {getIndexListenComplete(lineItem.summary)}
                  </TableCell>
                  <TableCell sx={{ fontSize: "0.875rem", py: 1.5 }}>
                    {formatNumber(lineItem.summary.totalUniqueUsers || 0)}
                  </TableCell>
                </>
              )}
              {/* Add additional metrics data for all formats */}
              <TableCell sx={{ fontSize: "0.875rem", py: 1.5 }}>
                {formatNumber(lineItem.summary.totalMeasurableImpressions || 0)}
              </TableCell>
              <TableCell sx={{ fontSize: "0.875rem", py: 1.5 }}>
                {formatPercentage(lineItem.summary.avgEngagementRate || 0)}
              </TableCell>
              <TableCell sx={{ fontSize: "0.875rem", py: 1.5 }}>
                {formatNumber(lineItem.summary.avgInteractionTime || 0)}
              </TableCell>
              <TableCell sx={{ fontSize: "0.875rem", py: 1.5 }}>
                {formatNumber(lineItem.summary.totalReachCoViewed || 0)}
              </TableCell>
              {isAdmin && (
                <TableCell sx={{ fontSize: "0.875rem", py: 1.5 }}>
                  {lineItem.summary.totalRealCost !== undefined
                    ? formatCurrency(lineItem.summary.totalRealCost, currency)
                    : "-"}
                </TableCell>
              )}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default LineItemsTable;
